import { AlertTriangle, ArrowRight, Building2, CheckCircle2, ClipboardList, Clock3, History, Wrench } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { fmtDate, fmtRelative } from '../../../shared/format'
import type { DetailTab } from '../../detailTabs'
import { isOverdue } from '../../taskFilters'
import { TASK_STATUSES, type Project } from '../../types'
import { SEVERITY_TONE, TASK_STATUS_DOT } from '../../visuals'
import { ProgressBar } from '../ProjectBits'
import { ProjectServersPanel } from '../ProjectServersPanel'
import { InstallationStatusBadge, SeverityBadge } from '../StatusBadges'
import { TaskMiniList } from '../task/TaskMiniList'
import { TeamPanel } from '../TeamPanel'
import { useHistoryText } from '../../history'

function Stat({ icon: Icon, value, label, tone }: { icon: typeof Clock3; value: number; label: string; tone: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card p-4 shadow-card">
      <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', tone)}>
        <Icon className="size-5" aria-hidden />
      </span>
      <div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  )
}

function Section({ title, action, children, className }: { title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('rounded-xl border bg-card shadow-card', className)}>
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <h3 className="flex-1 font-semibold">{title}</h3>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  )
}

export function OverviewTab({ project, onNavigate }: { project: Project; onNavigate: (tab: DetailTab) => void }) {
  const { t } = useTranslation()
  const historyText = useHistoryText()
  const open = project.tasks.filter((x) => x.status !== 'done')
  const overdue = open.filter(isOverdue)
  const upcoming = [...open].sort((a, b) => a.dueDate.localeCompare(b.dueDate)).slice(0, 6)
  const activity = project.tasks
    .flatMap((task) => task.history.map((h) => ({ ...h, task })))
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, 8)
  const blockers = project.installations.flatMap((i) =>
    i.blockers.filter((b) => !b.resolved).map((b) => ({ ...b, customer: i.customer, installationId: i.id })),
  )
  const seeAll = (tab: DetailTab) => (
    <Button size="sm" variant="ghost" onClick={() => onNavigate(tab)}>
      {t('projects.seeAll')}
      <ArrowRight />
    </Button>
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={ClipboardList} value={open.length} label={t('projects.overview.openTasks')} tone="bg-primary/10 text-primary" />
        <Stat icon={AlertTriangle} value={overdue.length} label={t('projects.overview.overdue')} tone="bg-destructive/10 text-destructive" />
        <Stat icon={Clock3} value={project.pendingApproval} label={t('projects.stats.pendingApproval')} tone="bg-violet/10 text-violet" />
        <Stat icon={Wrench} value={project.activeInstallations} label={t('projects.overview.activeInstallations')} tone="bg-success/10 text-success" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Section title={t('projects.overview.progress')} className="xl:col-span-2">
          <div className="space-y-4">
            <ProgressBar value={project.progress} label={t('projects.stats.progress')} />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {TASK_STATUSES.map((s) => (
                <button key={s} type="button" onClick={() => onNavigate('kanban')} className="rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={cn('size-2 rounded-full', TASK_STATUS_DOT[s])} aria-hidden />
                    {t(`projects.taskStatus.${s}`)}
                  </span>
                  <span className="mt-1 block text-xl font-semibold tabular-nums">{project.tasks.filter((x) => x.status === s).length}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{t('projects.overview.period', { start: fmtDate(project.startDate), end: fmtDate(project.endDate) })}</p>
          </div>
        </Section>

        <Section title={t('projects.overview.blockers', { count: blockers.length })} action={seeAll('installations')}>
          {blockers.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="size-4 text-success" aria-hidden />
              {t('projects.install.noBlockers')}
            </p>
          ) : (
            <ul className="-mx-2 space-y-1">
              {blockers.slice(0, 4).map((b) => (
                <li key={b.id}>
                  <Link
                    to={`/projects/${project.id}/installations?i=${b.installationId}`}
                    className="flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', SEVERITY_TONE[b.severity])}>
                      <AlertTriangle className="size-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="line-clamp-2 text-sm font-medium leading-snug">{b.text}</p>
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Building2 className="size-3.5 shrink-0" aria-hidden />
                        <span className="truncate">{b.customer}</span>
                        <span aria-hidden>·</span>
                        <span className="shrink-0">{fmtDate(b.date)}</span>
                      </p>
                    </div>
                    <SeverityBadge severity={b.severity} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Section title={t('projects.overview.upcoming')} action={seeAll('list')} className="xl:col-span-2">
          <TaskMiniList projectId={project.id} tasks={upcoming} emptyText={t('projects.overview.noUpcoming')} />
        </Section>
        <Section title={t('projects.overview.installations')} action={seeAll('installations')}>
          {project.installations.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('projects.install.empty')}</p>
          ) : (
            <ul className="space-y-3">
              {project.installations.slice(0, 5).map((i) => (
                <li key={i.id}>
                  <Link to={`/projects/${project.id}/installations?i=${i.id}`} className="block space-y-1.5 rounded-lg p-1 hover:bg-muted/50">
                    <span className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{i.customer}</span>
                      <InstallationStatusBadge status={i.status} />
                    </span>
                    <ProgressBar value={i.progress} label={i.customer} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <ProjectServersPanel project={project} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="rounded-xl border bg-card shadow-card">
          <TeamPanel project={project} />
        </section>
        <Section title={t('projects.overview.activity')}>
          {activity.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('projects.overview.noActivity')}</p>
          ) : (
            <ol className="space-y-3">
              {activity.map((h) => (
                <li key={h.id} className="flex gap-3 text-sm">
                  <History className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0">
                    <p>
                      <span className="font-medium">{h.by}</span>{' '}
                      {historyText(h)}{' '}
                      <Link to={`/projects/${project.id}/tasks/${h.task.id}`} className="font-mono text-xs text-primary hover:underline">
                        {h.task.key}
                      </Link>
                    </p>
                    <p className="text-xs text-muted-foreground">{fmtRelative(h.at)}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Section>
      </div>
    </div>
  )
}
